import { useEffect, useMemo, useState } from "react"
import {
  ChevronLeft,
  Lock,
  Plus,
  Search,
  SendHorizonal,
} from "lucide-react"
import { useProfileStore } from "../../../hooks/useProfileStore"
import { useAuthStore } from "../../../hooks/useAuthStore"
import { useQueryClient } from "@tanstack/react-query"
import {
  useConversationsQuery,
  useConversationMessagesQuery,
  useSendMessageMutation,
  useMarkConversationReadMutation,
} from "../../../api/query/useMessageQueries"
import socketService from "../../../api/socket"

const formatTime = (value) => {
  if (!value) return ""
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}


/**
 * An assistant's photo lives on their provider profile, a traveller's on their
 * user record, so accept either shape rather than assuming one.
 */
const resolveRecipientAvatar = (recipient = {}) => {
  const candidates = [
    recipient.profilePicture,
    recipient.profile?.profilePicture,
    recipient.avatar,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
    if (candidate?.url) return candidate.url;
  }
  return "";
};

function Avatar({ src, alt, name, size = "md" }) {
  const [imageFailed, setImageFailed] = useState(false)
  const initials = String(name || alt || "A")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase()

  const sizeClass =
    size === "lg"
      ? "h-14 w-14 text-base"
      : size === "sm"
      ? "h-12 w-12 text-sm"
      : "h-12 w-12 text-base"

  if (!src || imageFailed) {
    return (
      <div
        className={`flex shrink-0 items-center justify-center rounded-full bg-[#F3E8EE] font-semibold text-[#7A054A] ${sizeClass}`}
      >
        {initials}
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`shrink-0 rounded-full object-cover ${sizeClass}`}
      onError={() => setImageFailed(true)}
    />
  )
}

function ConversationItem({ conversation, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(conversation.id)}
      className={`flex w-full items-start gap-4 rounded-2xl px-4 py-4 text-left transition ${
        active ? "bg-[#F7F3F6]" : "hover:bg-[#F9FAFB]"
      }`}
    >
      <Avatar
        src={conversation.avatar}
        alt={conversation.name}
        name={conversation.name}
        size="sm"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-lg font-semibold text-[#1D2939]">
            {conversation.name}
          </p>
          {conversation.unread > 0 ? (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-[#FCE7F3] px-2 text-sm font-semibold text-[#A51B68]">
              {conversation.unread}
            </span>
          ) : null}
        </div>
        <p className="truncate text-base text-[#667085]">{conversation.preview}</p>
      </div>
    </button>
  )
}

function MessageBubble({ message }) {
  return (
    <div className={`flex flex-col ${message.mine ? "items-end" : "items-start"}`}>
      <div
        className={`max-w-[76%] rounded-2xl px-4 py-3 text-base leading-7 ${
          message.mine
            ? "rounded-br-md border border-[#E6E8EC] bg-white text-[#1D2939]"
            : "rounded-bl-md bg-[#F3F4F6] text-[#1D2939]"
        }`}
      >
        {message.text}
      </div>
      <span className="mt-1 px-1 text-sm text-[#98A2B3]">
        {formatTime(message.sentAt)}
      </span>
    </div>
  )
}

export default function Messages() {
  const queryClient = useQueryClient()
  const { profile } = useProfileStore()
  const { user } = useAuthStore()
  const currentUserId = user?.id || user?._id || ""

  const assistantName =
    profile?.fullName ||
    profile?.name ||
    user?.fullName ||
    user?.name ||
    "Aliu Omeza"

  const [search, setSearch] = useState("")
  const [activeConversationId, setActiveConversationId] = useState(null)
  const [draft, setDraft] = useState("")
  const [showMobileChat, setShowMobileChat] = useState(false)

  // 1. Fetch conversations from backend
  const { data: conversationsData } = useConversationsQuery()

  // Map backend conversations to the schema expected by frontend
  const conversations = useMemo(() => {
    const rawConversations = conversationsData?.data?.conversations || []
    return rawConversations.map((conv) => {
      const rec = conv.recipient || {}
      const lastMsg = conv.lastMessage
      const unreadCount = lastMsg && !lastMsg.isRead && lastMsg.sender !== currentUserId ? 1 : 0
      return {
        id: conv._id,
        name: `${rec.firstName || ""} ${rec.lastName || ""}`.trim() || "User",
        // No dummy stand-in: showing a stock photo of a stranger as someone's
        // profile picture is worse than no picture. Avatar falls back to the
        // person's initials, matching the traveller-side messages page.
        avatar: resolveRecipientAvatar(rec),
        status: rec.isOnline ? "Online" : "Offline",
        unread: unreadCount,
        preview: lastMsg?.text || "No messages yet",
        updatedAt: conv.updatedAt,
        recipient: rec,
        // Closed once the trip ends; reopens if the traveller books again.
        locked: Boolean(conv.isLocked),
      }
    })
  }, [conversationsData, currentUserId])

  // 2. Load active messages
  const { data: messagesData } = useConversationMessagesQuery(activeConversationId, {}, {
    enabled: !!activeConversationId,
  })

  const messages = useMemo(() => {
    const rawMessages = messagesData?.data?.items || []
    return rawMessages.map((msg) => {
      const senderId = msg.sender?._id || msg.sender
      return {
        id: msg._id,
        mine: senderId === currentUserId,
        text: msg.text,
        sentAt: msg.createdAt,
      }
    })
  }, [messagesData, currentUserId])

  const filteredConversations = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return conversations

    return conversations.filter((conversation) => {
      return (
        conversation.name.toLowerCase().includes(query) ||
        conversation.preview.toLowerCase().includes(query)
      )
    })
  }, [conversations, search])

  const activeConversation =
    conversations.find((conversation) => conversation.id === activeConversationId) ||
    filteredConversations[0] ||
    null

  // Set default active conversation on load
  useEffect(() => {
    if (!activeConversationId && filteredConversations.length > 0) {
      setActiveConversationId(filteredConversations[0].id)
    }
  }, [activeConversationId, filteredConversations])

  // Sockets Setup
  useEffect(() => {
    socketService.connect()

    const handleMessageReceived = (data) => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] })
      if (data?.conversationId) {
        queryClient.invalidateQueries({ queryKey: ["messages", data.conversationId] })
      } else {
        queryClient.invalidateQueries({ queryKey: ["messages"] })
      }
    }

    const handlePresenceChanged = () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] })
    }

    // A trip ending locks this thread, and the only signal this tab gets is the
    // notification push — without refetching, the composer stays visible until
    // the conversations query goes stale and every send bounces.
    const handleNotificationReceived = () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] })
    }

    socketService.on("message", handleMessageReceived)
    socketService.on("presence", handlePresenceChanged)
    socketService.on("notification", handleNotificationReceived)

    return () => {
      socketService.off("message", handleMessageReceived)
      socketService.off("presence", handlePresenceChanged)
      socketService.off("notification", handleNotificationReceived)
    }
  }, [queryClient])

  const sendMessageMutation = useSendMessageMutation()
  const markReadMutation = useMarkConversationReadMutation()

  const handleSelectConversation = (conversationId) => {
    setActiveConversationId(conversationId)
    setShowMobileChat(true)
    markReadMutation.mutate(conversationId)
  }

  const handleSendMessage = (event) => {
    event.preventDefault()
    const value = draft.trim()
    if (!value || !activeConversation || activeConversation.locked) return

    sendMessageMutation.mutate(
      {
        recipientId: activeConversation.recipient?._id,
        text: value,
      },
      {
        onSuccess: () => {
          setDraft("")
        },
      }
    )
  }

  return (
    <div
      className="grid h-[calc(100vh-8.5rem)] min-h-[620px] gap-4 lg:grid-cols-[380px_minmax(0,1fr)]"
      title={`Inbox for ${assistantName}`}
    >
      <section
        className={`min-h-0 flex-col rounded-xl border border-[#EEF0F3] bg-white ${
          showMobileChat ? "hidden lg:flex" : "flex"
        }`}
      >
        <div className="border-b border-[#EEF0F3] px-5 py-5">
          <label className="flex items-center gap-3 rounded-full border border-[#D0D5DD] px-4 py-3">
            <Search className="h-5 w-5 text-[#98A2B3]" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search anything"
              className="w-full border-0 bg-transparent text-lg text-[#1D2939] outline-none placeholder:text-[#98A2B3]"
            />
          </label>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {filteredConversations.length === 0 ? (
            <div className="px-3 py-6 text-center text-lg text-[#667085]">
              No conversations match your search.
            </div>
          ) : (
            filteredConversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                active={conversation.id === activeConversation?.id}
                onSelect={handleSelectConversation}
              />
            ))
          )}
        </div>
      </section>

      <section
        className={`min-h-0 flex-col rounded-xl border border-[#EEF0F3] bg-white ${
          showMobileChat ? "flex" : "hidden lg:flex"
        }`}
      >
        {activeConversation ? (
          <>
            <div className="flex items-start justify-between border-b border-[#EEF0F3] px-6 py-5">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setShowMobileChat(false)}
                  className="rounded-full p-2 text-[#667085] transition hover:bg-[#F2F4F7] lg:hidden"
                  aria-label="Back to conversations"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <Avatar
                  src={activeConversation.avatar}
                  alt={activeConversation.name}
                  name={activeConversation.name}
                  size="lg"
                />
                <div>
                  <p className="text-xl font-semibold text-[#1D2939]">
                    {activeConversation.name}
                  </p>
                  <p className="text-base text-[#12B76A]">
                    {activeConversation.status}
                  </p>
                </div>
              </div>

            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
              <div className="flex min-h-full flex-col justify-end gap-5">
                {messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </div>
            </div>

            {activeConversation.locked ? (
              <div className="border-t border-[#EEF0F3] px-6 py-6">
                <div className="flex flex-col items-center gap-3 rounded-2xl bg-[#F9FAFB] px-6 py-6 text-center">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F8EEF4]">
                    <Lock className="h-5 w-5 text-[#61013B]" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-[#1D2939]">
                      This conversation is closed
                    </p>
                    <p className="mt-1 text-base text-[#667085]">
                      Your trip with {activeConversation.name} has ended. It
                      reopens automatically if they book you again.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
            <div className="border-t border-[#EEF0F3] px-6 py-5">
              <form
                onSubmit={handleSendMessage}
                className="flex items-center gap-3 rounded-full border border-[#D0D5DD] bg-white px-4 py-3"
              >
                <button
                  type="button"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D0D5DD] text-[#667085]"
                  aria-label="Add attachment"
                >
                  <Plus className="h-5 w-5" />
                </button>

                <input
                  type="text"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="Type your message..."
                  className="w-full border-0 bg-transparent text-lg text-[#1D2939] outline-none placeholder:text-[#98A2B3]"
                />

                <button
                  type="submit"
                  disabled={!draft.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#61013B] text-white transition disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label={`Send message to ${activeConversation.name}`}
                >
                  <SendHorizonal className="h-5 w-5" />
                </button>
              </form>
            </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center px-6 text-center text-lg text-[#667085]">
            No conversation selected.
          </div>
        )}
      </section>
    </div>
  )
}
